import { Badge } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { Disclosure } from '@kerfjs/ui/disclosure';
import { Grid } from '@kerfjs/ui/grid';
import { List } from '@kerfjs/ui/list';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { TextField } from '@kerfjs/ui/text-field';
import { type SafeHtml } from 'kerfjs';
import { Lock } from 'lucide';

import type { DemandNote } from '../../shared/demand-notes.js';
import type { IntakeLine } from '../data/state.js';
import { DemandNotes } from './demand-notes/index.js';

/**
 * Adds or edits one demand line. With `readOnly` (a submitted demand seen by its requester, or a
 * cancelled demand) the same form opens for viewing: every field is `readonly`, the header carries
 * a Read Only badge, and the footer offers only Close (HS2-SECM6V). An existing line also shows its
 * append-only notes, which stay open while the demand's notes are (HS2-JRQT8A).
 */
export function DemandLineItemEditorDialog({
  line,
  lightDismiss = false,
  readOnly = false,
  notes,
  notesReadOnly = readOnly,
  memberId,
  editingNoteId = null,
  advancedOpen,
}: {
  line: IntakeLine | undefined;
  lightDismiss?: boolean;
  readOnly?: boolean;
  /** The demand's notes; an existing line shows its own list (HS2-JRQT8A). Omitted, no notes section. */
  notes?: readonly DemandNote[];
  /** Notes stay open for a requester on a submitted demand; only a closed or cancelled demand locks them. */
  notesReadOnly?: boolean;
  memberId?: string | null;
  editingNoteId?: string | null;
  /** The person's disclosure choice survives intake rerenders while this editor is open. */
  advancedOpen?: boolean;
}): SafeHtml {
  const adding = line === undefined;
  const viewing = readOnly && !adding;
  const title = adding
    ? 'Add line item'
    : viewing
      ? `Line ${String(line.lineNumber)}`
      : `Edit line ${String(line.lineNumber)}`;
  // Advanced opens when it holds something to read; a viewer also sees a duty without a code.
  const hasHsDetails = (line?.hsCode ?? '') !== '' || (viewing && (line.hsCodeDutyPct ?? '') !== '');
  return (
    <DialogSurface
      label={title}
      className="demand-line-item-editor-dialog"
      lightDismiss={lightDismiss || viewing}
      rootAttributes={{
        'data-unsaved-guard': viewing ? undefined : 'this line item',
        'data-read-only': viewing ? '' : undefined,
        'data-auto-open-dialog': '',
      }}
      headerActions={
        viewing ? (
          <Badge
            tone="neutral"
            shape="pill"
            icon={<LucideIcon icon={Lock} name="" />}
            title="This demand can't be changed right now, so its line items are view only."
          >
            Read Only
          </Badge>
        ) : undefined
      }
      footer={
        viewing ? (
          <Button
            tone="neutral"
            size="default"
            emphasis="primary"
            action="line-editor-cancel"
            rootAttributes={{ 'data-dialog-close': '' }}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              tone="neutral"
              size="default"
              emphasis="plain"
              action="line-editor-cancel"
              rootAttributes={{ 'data-unsaved-cancel': '', 'data-dialog-close': '' }}
            >
              Cancel
            </Button>
            <Button
              size="default"
              tone="brand"
              emphasis="primary"
              action="line-editor-save"
              rootAttributes={{ 'data-unsaved-save': '' }}
            >
              {adding ? 'Add item' : 'Save item'}
            </Button>
          </>
        )
      }
    >
      <List gap="m">
        <List gap="m" controlInsets="rl">
          {/* The assistant normalizes the wording in the background (HS2-6ZNBWY), so the
            dialog no longer asks for a normalized specification. */}
          <TextField
            type="text"
            name="line-editor-description"
            readOnly={viewing}
            label="Description as Written"
            value={line?.description ?? ''}
            autofocus={!viewing}
          />
          <Grid columns={2} gap="m" flex>
            <TextField
              name="line-editor-quantity"
              readOnly={viewing}
              type="number"
              label="Quantity"
              value={line?.quantity ?? ''}
            />
            <TextField type="text" name="line-editor-unit" readOnly={viewing} label="Unit" value={line?.unit ?? ''} />
          </Grid>
        </List>
        <Disclosure
          title="Advanced"
          appearance="sunken"
          open={advancedOpen ?? hasHsDetails}
          toggleAction="line-editor-advanced-toggle"
        >
          <Grid columns={2} gap="m" flex>
            <TextField
              type="text"
              name="line-editor-hs-code"
              readOnly={viewing}
              label="HS Code"
              value={line?.hsCode ?? ''}
              hint={viewing ? undefined : 'Optional. A manual code is treated as confirmed.'}
            />
            <TextField
              name="line-editor-hs-duty"
              readOnly={viewing}
              type="number"
              min="0"
              max="100"
              step="0.01"
              label="Customs Duty (%)"
              value={line?.hsCodeDutyPct ?? ''}
              hint={viewing ? undefined : 'Optional'}
            />
          </Grid>
        </Disclosure>
        {line !== undefined && notes !== undefined ? (
          <DemandNotes
            notes={notes}
            lineItemId={line.id}
            readOnly={notesReadOnly}
            memberId={memberId}
            editingNoteId={editingNoteId}
            headingId="line-notes-label"
          />
        ) : null}
      </List>
    </DialogSurface>
  );
}
